/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

import { supabase } from "./supabaseClient.js";

// =====================================================================
// accountService  ::  multi-tenant workspace + membership over RLS
//
// Tenancy = `accounts`; users belong via `account_members(role)`. All access
// is enforced by RLS + security-definer RPCs (0010–0013). No edge function:
// invites are pending rows that the invitee claims on login.
// =====================================================================

const ROLES = new Set(["admin", "member"]);

// Workspaces the signed-in user belongs to, with their role. Returns the
// one they manage first (owner/admin), then the rest.
export async function listMyAccounts() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  const { data, error } = await supabase
    .from("account_members")
    .select("role, created_at, account_id, accounts(name, created_by, auto_created, logo_url, primary_color, secondary_color, spec_unit_system)")
    .eq("user_id", user.id)
    .order("created_at", { ascending: true });
  if (error) {
    console.error("[accountService.listMyAccounts]", error);
    throw new Error("Could not load your workspaces.");
  }
  return (data ?? []).map((r) => ({
    accountId: r.account_id,
    role: r.role,
    name: r.accounts?.name ?? "Workspace",
    logoUrl: r.accounts?.logo_url ?? null,
    primaryColor: r.accounts?.primary_color ?? null,
    secondaryColor: r.accounts?.secondary_color ?? null,
    specUnitSystem: r.accounts?.spec_unit_system === "metric" ? "metric" : "us",
    isOwner: r.accounts?.created_by === user.id,
    autoCreated: r.accounts?.auto_created === true,
  }));
}

// =====================================================================
// Active-workspace override
//
// Until now every module resolved its tenant through getMyPrimaryAccount(),
// which PICKS one — so a user who belongs to two workspaces was permanently
// stuck in whichever the pick returned, with no way to reach the other. That is
// a real gap for any multi-workspace customer, not only for demoing.
//
// The choice is stored per user, so switching on one machine doesn't move a
// colleague, and signing out of a shared browser doesn't hand the next person a
// surprising workspace.
//
// ⚠️ THIS IS A PREFERENCE, NOT A PERMISSION. localStorage is caller-controlled,
// so the stored id is ALWAYS validated against the user's real memberships
// before it is honoured; an id they no longer belong to is ignored and cleared.
// RLS remains the actual boundary — editing this value can only ever select
// among workspaces the database already lets them see.
// =====================================================================
const ACTIVE_KEY_PREFIX = "bc.activeAccount.";

function activeKeyFor(userId) { return `${ACTIVE_KEY_PREFIX}${userId}`; }

function readStoredAccount(userId) {
  try { return localStorage.getItem(activeKeyFor(userId)); } catch { return null; }
}

/** Remember the workspace to use. Pass null to fall back to the default pick. */
export function setActiveAccountId(userId, accountId) {
  try {
    if (accountId) localStorage.setItem(activeKeyFor(userId), accountId);
    else localStorage.removeItem(activeKeyFor(userId));
  } catch { /* private mode — the default pick still works */ }
}

// The user's main workspace — every module resolves its tenant through this.
//
// Signup auto-creates a placeholder personal workspace (handle_new_user_account),
// so someone who signs up and is THEN invited to a real workspace has two
// memberships. Picking the earliest 'owner' used to land them in the empty
// placeholder and show them an empty app. A real workspace always wins now;
// the placeholder is only used when it's all they have.
export async function getMyPrimaryAccount() {
  const accounts = await listMyAccounts();
  if (accounts.length === 0) return null;

  // An explicit choice wins — but only if they still belong to it. A stale or
  // tampered id falls through to the default pick and is cleared, so revoking
  // someone's membership takes effect on their next read rather than leaving
  // them pointed at a workspace that now fails every query.
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const stored = readStoredAccount(user.id);
    if (stored) {
      const match = accounts.find((a) => a.accountId === stored);
      if (match) return match;
      setActiveAccountId(user.id, null);
    }
  }

  const pick = (list) =>
    list.find((a) => a.role === "owner") ||
    list.find((a) => a.role === "admin") ||
    list[0];

  const real = accounts.filter((a) => !a.autoCreated);
  return real.length ? pick(real) : pick(accounts);
}

const ACCOUNT_FIELDS = new Set(["name", "logo_url", "primary_color", "secondary_color", "spec_unit_system"]);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export async function updateAccount(accountId, patch) {
  const out = {};
  for (const k of ACCOUNT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  if (out.name != null) {
    const n = String(out.name).trim();
    if (n.length < 1 || n.length > 120) throw new Error("Company name must be 1–120 characters.");
    out.name = n;
  }
  // Brand colors: accept a #rrggbb hex or null (to clear). Reject anything else
  // so we never store unsanitized strings that later render into a PDF/UI.
  for (const k of ["primary_color", "secondary_color"]) {
    if (k in out) {
      if (out[k] == null || out[k] === "") { out[k] = null; continue; }
      const v = String(out[k]).trim().toLowerCase();
      if (!HEX_COLOR.test(v)) throw new Error("Colors must be a hex value like #1B2A4A.");
      out[k] = v;
    }
  }
  // Product-spec unit system: only the two known values are accepted.
  if ("spec_unit_system" in out) {
    out.spec_unit_system = out.spec_unit_system === "metric" ? "metric" : "us";
  }
  if (Object.keys(out).length === 0) return;

  const { error } = await supabase.from("accounts").update(out).eq("id", accountId);
  if (error) {
    console.error("[accountService.updateAccount]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only owners and admins can change workspace settings.");
    }
    throw new Error("Could not save workspace settings.");
  }
}

// Set the sheet-wide Product Specifications unit system ('us' | 'metric').
// Owner/admin only (enforced by the accounts UPDATE RLS).
export async function setSpecUnitSystem(accountId, system) {
  await updateAccount(accountId, { spec_unit_system: system === "metric" ? "metric" : "us" });
}

// Upload a company logo to account-logos/<accountId>/logo.<ext> and store the URL.
export async function uploadAccountLogo(accountId, file) {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Use a PNG, JPG, or WebP image.");
  }
  if (file.size > 2 * 1024 * 1024) throw new Error("Logo must be under 2 MB.");

  const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
  const path = `${accountId}/logo.${ext}`;
  const { error: upErr } = await supabase.storage
    .from("account-logos")
    .upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) {
    console.error("[accountService.uploadAccountLogo]", upErr);
    throw new Error("Could not upload the logo.");
  }
  const { data } = supabase.storage.from("account-logos").getPublicUrl(path);
  const url = `${data.publicUrl}?t=${Date.now()}`; // cache-bust
  await updateAccount(accountId, { logo_url: url });
  return url;
}

// Full roster (with emails) — uses the definer RPC since auth.users isn't
// client-readable.
export async function listMembers(accountId) {
  const { data, error } = await supabase.rpc("account_members_with_emails", { acct: accountId });
  if (error) {
    console.error("[accountService.listMembers]", error);
    throw new Error("Could not load members.");
  }
  return data ?? [];
}

export async function listPendingInvites(accountId) {
  const { data, error } = await supabase
    .from("invitations")
    .select("id, email, role, created_at, expires_at")
    .eq("account_id", accountId)
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[accountService.listPendingInvites]", error);
    throw new Error("Could not load invitations.");
  }
  return data ?? [];
}

export async function inviteMember(accountId, email, role = "member") {
  const clean = String(email || "").trim().toLowerCase();
  if (!/^\S+@\S+\.\S+$/.test(clean)) throw new Error("Enter a valid email address.");
  if (!ROLES.has(role)) throw new Error("Invalid role.");

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  const { error } = await supabase
    .from("invitations")
    .insert({ account_id: accountId, email: clean, role, invited_by: user.id });
  if (error) {
    console.error("[accountService.inviteMember]", error);
    if (error.code === "23505") throw new Error("That person is already invited.");
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only owners and admins can invite people.");
    }
    throw new Error("Could not send the invite.");
  }
}

export async function revokeInvite(inviteId) {
  const { error } = await supabase
    .from("invitations")
    .update({ status: "revoked" })
    .eq("id", inviteId);
  if (error) {
    console.error("[accountService.revokeInvite]", error);
    throw new Error("Could not revoke the invite.");
  }
}

export async function changeMemberRole(accountId, userId, role) {
  if (!ROLES.has(role)) throw new Error("Invalid role.");
  const { error } = await supabase
    .from("account_members")
    .update({ role })
    .eq("account_id", accountId)
    .eq("user_id", userId);
  if (error) {
    console.error("[accountService.changeMemberRole]", error);
    throw new Error("Could not change the role.");
  }
}

export async function removeMember(accountId, userId) {
  const { error } = await supabase
    .from("account_members")
    .delete()
    .eq("account_id", accountId)
    .eq("user_id", userId);
  if (error) {
    console.error("[accountService.removeMember]", error);
    throw new Error("Could not remove the member.");
  }
}

// Called after login: join any account that invited this email.
export async function claimMyInvitations() {
  const { data, error } = await supabase.rpc("claim_my_invitations");
  if (error) {
    // Non-fatal — log and move on.
    console.error("[accountService.claimMyInvitations]", error);
    return 0;
  }
  return data ?? 0;
}

// Called after login: promote this user to Brand Command staff (platform_admins)
// if their email was invited via the admin Team tab (0040). Cross-cutting session
// bootstrap — calls the RPC by name only, so /shared stays free of module imports.
export async function claimMyPlatformAdminInvitations() {
  const { data, error } = await supabase.rpc("claim_my_platform_admin_invitations");
  if (error) {
    // Non-fatal — a normal customer simply has no staff invite to claim.
    console.error("[accountService.claimMyPlatformAdminInvitations]", error);
    return 0;
  }
  return data ?? 0;
}
